import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  Stack
} from "@mui/material";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import { useSelector } from "react-redux";
import { RootState } from "common/redux/core/root.reducer";
import { ChatbotService } from "../data/services/chatbot.service";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import "./AIChatbot.scss";

// types
type MenuProps = import("@mui/material/Menu").MenuProps;
type SelectChangeEvent<T = string> =
  import("@mui/material/Select").SelectChangeEvent<T>;

type Option = { value: string; label: string; raw?: string | number };
type FlowOption = {
  value: string;
  label: string;
  configID: number | string;
  docTypeId?: number | string;
  raw?: any;
};
type ChatMessage = {
  id: number;
  sender: "user" | "bot";
  text: string;
  timestamp: string;
};

const BRAND_PRIMARY = "#1777CF";
const BRAND_PRIMARY_HOVER = "#155CB4";
const TEXT_SECONDARY = "#4B5563";

const menuProps: Partial<MenuProps> = {
  PaperProps: { style: { minWidth: 300, maxWidth: "70%" } }
};

const DOC_TYPE_LABEL_ID_MAP: Record<string, number> = {
  engineering: 1,
  contract: 2,
  invoice: 3,
  others: 4,
  doc: 5
};

interface AIChatbotPageProps {
  showHeader?: boolean;
  variant?: "page" | "floating";
}

const AIChatbotPage: React.FC<AIChatbotPageProps> = ({
  showHeader = true,
  variant = "page"
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isFloating = variant === "floating";

  const pageClassName = `ai-chatbot-page${
    isFloating ? " ai-chatbot-page--floating" : ""
  }`;
  const containerClassName = `ai-chatbot-container${
    isFloating ? " ai-chatbot-container--floating" : ""
  }`;
  const headerClassName = `ai-chatbot-header${
    isFloating ? " ai-chatbot-header--floating" : ""
  }`;
  const mainClassName = `ai-chatbot-main${
    isFloating ? " ai-chatbot-main--floating" : ""
  }`;

  const docTypeFromUrl = useMemo(
    () => searchParams.get("documentType"),
    [searchParams]
  );

  // Dropdowns and selections
  const [documentType, setDocumentType] = useState<string>("");
  const [flowName, setFlowName] = useState<string>("");
  const [fileName, setFileName] = useState<string[]>([]);
  const [flowOptions, setFlowOptions] = useState<FlowOption[]>([]);
  const [fileOptions, setFileOptions] = useState<Option[]>([]);
  const [loadingFlows, setLoadingFlows] = useState<boolean>(false);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(false);

  // Chat state
  const [queryText, setQueryText] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showTyping, setShowTyping] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [messagesTab0, setMessagesTab0] = useState<ChatMessage[]>([]);
  const [messagesTab1, setMessagesTab1] = useState<ChatMessage[]>([]);

  const [errors, setErrors] = useState<{
    documentType?: string;
    flowName?: string;
    fileName?: string;
    queryText?: string;
  }>({});

  const chatWindowRef = useRef<HTMLDivElement>(null);

  const { documentTypeOptions } = useSelector(
    (state: RootState) => state.documentType
  );
  const docTypeOptionList = useMemo(
    () => documentTypeOptions ?? [],
    [documentTypeOptions]
  );

  const selectedDocumentTypeOption = useMemo(
    () => docTypeOptionList.find((opt) => String(opt.value) === documentType),
    [docTypeOptionList, documentType]
  );

  // Initialization & URL Params
  useEffect(() => {
    if (isFloating || !docTypeFromUrl || docTypeOptionList.length === 0) {
      return;
    }
    const matchingOption = docTypeOptionList.find(
      (opt) => opt.label === docTypeFromUrl
    );
    if (!matchingOption) {
      return;
    }
    const optionValue = String(matchingOption.value);
    if (documentType === optionValue) {
      return;
    }
    setDocumentType(optionValue);
    setLoadingFlows(true);
    fetchFlows(matchingOption.label).finally(() => setLoadingFlows(false));
  }, [docTypeFromUrl, documentType, docTypeOptionList, isFloating]);

  useEffect(() => {
    chatWindowRef.current?.scrollTo({
      top: chatWindowRef.current.scrollHeight,
      behavior: "smooth"
    });
  }, [messagesTab0.length, messagesTab1.length, showTyping]);

  const validateForm = () => {
    const newErrors: typeof errors = {};
    if (!documentType || !selectedDocumentTypeOption) {
      newErrors.documentType = "Document type is required.";
    }
    if (!flowName) newErrors.flowName = "Flow name is required.";
    if (!queryText.trim()) newErrors.queryText = "Query is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const selectedFlowOption = flowOptions.find(
      (opt) => opt.value === flowName
    );
    const normalizedConfigId = (() => {
      const rawConfigId = selectedFlowOption?.configID;
      if (typeof rawConfigId === "number") {
        return rawConfigId;
      }
      const parsed = Number(rawConfigId);
      return Number.isNaN(parsed) ? rawConfigId ?? "" : parsed;
    })();

    const normalizedFlowId = (() => {
      const rawFlowId = selectedFlowOption?.value ?? flowName;
      if (typeof rawFlowId === "number") {
        return rawFlowId;
      }
      const parsed = Number(rawFlowId);
      return Number.isNaN(parsed) ? rawFlowId : parsed;
    })();

    const docTypeIdForPayload = (() => {
      const resolveNumeric = (input: any) => {
        if (typeof input === "number") {
          return input;
        }
        const parsed = Number(input);
        return Number.isNaN(parsed) ? undefined : parsed;
      };

      const rawDocTypeId =
        selectedFlowOption?.docTypeId ?? selectedDocumentTypeOption?.value;
      const numericFromRaw = resolveNumeric(rawDocTypeId);
      if (typeof numericFromRaw === "number") {
        return numericFromRaw;
      }

      const docTypeLabelCandidate =
        selectedFlowOption?.raw?.document_type ??
        selectedFlowOption?.raw?.doc_type ??
        selectedDocumentTypeOption?.label ??
        docTypeOptionList.find((opt) => String(opt.value) === documentType)
          ?.label ??
        (typeof rawDocTypeId === "string" ? rawDocTypeId : undefined);

      if (docTypeLabelCandidate) {
        const normalizedLabel = docTypeLabelCandidate.trim().toLowerCase();
        const mappedId = DOC_TYPE_LABEL_ID_MAP[normalizedLabel];
        if (typeof mappedId === "number") {
          return mappedId;
        }
        const orderBasedIndex = docTypeOptionList.findIndex(
          (opt) => opt.label.toLowerCase() === normalizedLabel
        );
        if (orderBasedIndex >= 0) {
          return orderBasedIndex + 1;
        }
      }

      return undefined;
    })();

    const selectedDocIds = fileName
      .map((selected) => {
        const match = fileOptions.find((opt) => opt.value === selected);
        const rawValue = match?.raw ?? selected;
        if (typeof rawValue === "number") {
          return rawValue;
        }
        const parsed = Number(rawValue);
        return Number.isNaN(parsed) ? rawValue : parsed;
      })
      .filter(
        (val): val is string | number =>
          val !== undefined &&
          val !== null &&
          !(typeof val === "number" && Number.isNaN(val))
      );

    if (docTypeIdForPayload === undefined) {
      setErrors((prev) => ({
        ...prev,
        documentType: "Document type is not mapped to an ID."
      }));
      return;
    }

    const payload = {
      config_id: normalizedConfigId,
      docTypeId: docTypeIdForPayload,
      flow_id: normalizedFlowId,
      doc_id: selectedDocIds,
      user_query: queryText.trim(),
      mode: tabValue === 0 ? "chat" : "document"
    };

    const timestamp = new Date().toLocaleTimeString();
    const userMessage: ChatMessage = {
      id: Date.now(),
      sender: "user",
      text: queryText.trim(),
      timestamp
    };

    if (tabValue === 0) setMessagesTab0((prev) => [...prev, userMessage]);
    else setMessagesTab1((prev) => [...prev, userMessage]);

    setQueryText("");
    setSubmitting(true);
    setShowTyping(true);

    try {
      const response = await ChatbotService.sendChatMessage(payload);
      setShowTyping(false);
      const botMessage: ChatMessage = {
        id: Date.now() + 1,
        sender: "bot",
        text:
          (response as any).agent_response || "Response received successfully",
        timestamp: new Date().toLocaleTimeString()
      };
      if (tabValue === 0) setMessagesTab0((prev) => [...prev, botMessage]);
      else setMessagesTab1((prev) => [...prev, botMessage]);
    } catch (error: any) {
      setShowTyping(false);
      const errorMessage: ChatMessage = {
        id: Date.now() + 1,
        sender: "bot",
        text: `Error: ${
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong"
        }`,
        timestamp: new Date().toLocaleTimeString()
      };
      if (tabValue === 0) setMessagesTab0((prev) => [...prev, errorMessage]);
      else setMessagesTab1((prev) => [...prev, errorMessage]);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchFlows = async (docType: string) => {
    try {
      const response = await ChatbotService.fetchFlowsByDocumentType(docType);
      const data = (response as any)?.data;
      if (data) {
        const transformedConfigurations = data
          .map((config: any) => {
            const flowIdCandidate =
              config?.flow_id ??
              config?.flowId ??
              config?.id ??
              config?.config_flow_id;

            const configIdCandidate =
              config?.config_id ??
              config?.configId ??
              config?.configuration_id ??
              config?.configurationId;

            const docTypeIdCandidate =
              config?.document_type_id ??
              config?.document_typeId ??
              config?.doc_type_id ??
              config?.docTypeId ??
              config?.documentTypeId ??
              config?.document_type_information_id ??
              config?.documentTypeInformationId ??
              config?.document_type ??
              config?.doc_type ??
              config?.docType;

            const normalizeNumeric = (input: any) => {
              if (typeof input === "number") {
                return input;
              }
              const parsed = Number(input);
              return Number.isNaN(parsed) ? input : parsed;
            };

            const resolvedFlowId =
              flowIdCandidate ?? configIdCandidate ?? config?.flow_name;
            if (resolvedFlowId === undefined || resolvedFlowId === null) {
              return null;
            }

            return {
              value: String(resolvedFlowId),
              label: config?.flow_name ?? config?.flowName ?? "",
              configID: normalizeNumeric(configIdCandidate ?? ""),
              docTypeId: normalizeNumeric(docTypeIdCandidate),
              raw: config
            };
          })
          .filter((option: FlowOption | null): option is FlowOption =>
            Boolean(option?.value)
          );
        setFlowOptions(transformedConfigurations);
      }
    } catch (error) {
      console.error("Error fetching flows:", error);
    }
  };

  const fetchFileListByFlow = async (flowID: number | string) => {
    try {
      const response = await ChatbotService.fetchDocumentsByFlow(
        flowID,
        1,
        1000
      );
      const apiPayload = (response as any)?.data;

      const rawItems: any[] = Array.isArray(apiPayload?.items)
        ? apiPayload.items
        : Array.isArray(apiPayload)
        ? apiPayload
        : [];

      const normalizedOptions = rawItems.map((item) => {
        const labelCandidate =
          item?.document_name ??
          item?.visible_filename ??
          item?.visiable_filename ??
          item?.file_display_name ??
          item?.files_name ??
          item?.file_name ??
          item?.fileName ??
          item?.filename ??
          item?.name ??
          "Unnamed document";

        const idCandidate =
          item?.id ??
          item?.document_id ??
          item?.documentId ??
          item?.document_guid ??
          item?.documentGuid ??
          item?.guid ??
          item?.file_id ??
          labelCandidate;

        return {
          label: String(labelCandidate),
          value: String(idCandidate),
          raw: idCandidate
        };
      });

      setFileOptions(normalizedOptions);
    } catch (error: any) {
      console.error("Error fetching documents:", error);
      setFileOptions([]);
    }
  };

  // Handlers
  const handleDocumentTypeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setDocumentType(value);
    setFlowName("");
    setFileName([]);
    setFlowOptions([]);
    setFileOptions([]);
    const selectedOption = docTypeOptionList.find(
      (opt) => String(opt.value) === value
    );
    const docTypeParam = selectedOption?.label ?? value;
    if (!isFloating) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("documentType", docTypeParam);
      setSearchParams(newParams);
    }
    if (!docTypeParam) {
      return;
    }
    setLoadingFlows(true);
    fetchFlows(docTypeParam).finally(() => setLoadingFlows(false));
  };

  const handleFlowNameChange = (event: SelectChangeEvent<string>) => {
    const selectedFlow = event.target.value;
    setFlowName(selectedFlow);
    setFileName([]);
    setFileOptions([]);
    const numericFlowId = Number(selectedFlow);
    const flowIdentifier = Number.isNaN(numericFlowId)
      ? selectedFlow
      : numericFlowId;
    setLoadingFiles(true);
    fetchFileListByFlow(flowIdentifier).finally(() => setLoadingFiles(false));
  };

  const handleFileChange = (event: SelectChangeEvent<string[]>) => {
    const rawValue = event.target.value;
    const value = typeof rawValue === "string" ? rawValue.split(",") : rawValue;
    if (value.includes("__ALL__")) {
      if (fileName.length === fileOptions.length) {
        setFileName([]);
      } else {
        setFileName(fileOptions.map((opt) => opt.value));
      }
      return;
    }
    setFileName(value);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // FAQ items
  const faqItems = [
    {
      question: "How many documents were uploaded?",
      answer: "Use the Files dropdown to review available documents."
    },
    {
      question: "List of documents which were rejected?",
      answer: "Yes, select multiple files from the Files dropdown."
    },
    {
      question: "List of documents which failed to process?",
      answer: "Responses are AI-generated based on document content."
    }
  ];

  const handleQuery = (q: string) => setQueryText(q);
  const currentMessages = tabValue === 0 ? messagesTab0 : messagesTab1;

  return (
    <>
      {showHeader && <HeaderNew showSearch={false} />}
      <Box className={pageClassName}>
        <Box className={containerClassName}>
          <Box className={headerClassName}>
            <Box>
              <Box className="ai-chatbot-title-row">
                <Typography variant="h5" fontWeight="600" color="text.primary">
                  Chatbot Hub
                </Typography>
                <Box className="ai-chatbot-title-dots">
                  <Box className="ai-chatbot-title-dot" />
                  <Box className="ai-chatbot-title-dot" />
                  <Box className="ai-chatbot-title-dot" />
                </Box>
              </Box>
              <Typography
                variant="body1"
                sx={{ color: TEXT_SECONDARY }}
                className="ai-chatbot-subtitle"
              >
                Query your contracts and get drafting assistance
              </Typography>
            </Box>
            {!isFloating && (
              <Button
                variant="outlined"
                size="small"
                sx={{ minWidth: 44, p: 0.75 }}
                aria-label="Help"
              >
                <HelpOutlineIcon fontSize="small" />
              </Button>
            )}
          </Box>

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            className="ai-chatbot-tabs"
          >
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <DeviceHubIcon fontSize="medium" />
                  Chat with Data
                </Box>
              }
              value={0}
            />
            <Tab
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <FilePresentIcon fontSize="medium" />
                  Chat with Document
                </Box>
              }
              value={1}
            />
          </Tabs>

          <Box className={mainClassName}>
            {/* Left - Filters and Chat */}
            <Box className="ai-chatbot-left">
              {/* Filters */}
              <Box className="ai-chatbot-filters">
                <FormControl
                  size="small"
                  fullWidth
                  error={!!errors.documentType}
                  disabled={docTypeOptionList.length === 0}
                >
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    label="Document Type"
                    value={documentType}
                    onChange={handleDocumentTypeChange}
                    renderValue={(selectedValue) => {
                      const normalizedValue = Array.isArray(selectedValue)
                        ? selectedValue[0]
                        : selectedValue;
                      return (
                        docTypeOptionList.find(
                          (opt) => String(opt.value) === String(normalizedValue)
                        )?.label ?? ""
                      );
                    }}
                  >
                    {docTypeOptionList.map((opt) => (
                      <MenuItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!errors.documentType && (
                    <Typography variant="caption" color="error">
                      {errors.documentType}
                    </Typography>
                  )}
                </FormControl>

                <FormControl
                  size="small"
                  fullWidth
                  error={!!errors.flowName}
                  disabled={
                    !documentType || loadingFlows || flowOptions?.length == 0
                  }
                >
                  <InputLabel>
                    {loadingFlows ? "Loading flows..." : "Flow Name"}
                  </InputLabel>
                  <Select
                    label={loadingFlows ? "Loading flows..." : "Flow Name"}
                    value={flowName}
                    onChange={handleFlowNameChange}
                  >
                    {flowOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!errors.flowName && (
                    <Typography variant="caption" color="error">
                      {errors.flowName}
                    </Typography>
                  )}
                </FormControl>

                <FormControl
                  size="small"
                  fullWidth
                  error={!!errors.fileName}
                  disabled={
                    !flowName || loadingFiles || fileOptions?.length == 0
                  }
                >
                  <InputLabel>
                    {loadingFiles ? "Loading files..." : "Files"}
                  </InputLabel>
                  <Select
                    label={loadingFiles ? "Loading files..." : "Files"}
                    multiple
                    value={fileName}
                    onChange={handleFileChange}
                    renderValue={(selected) =>
                      fileOptions
                        .filter((opt) =>
                          (selected as string[]).includes(opt.value)
                        )
                        .map((opt) => opt.label)
                        .join(", ")
                    }
                    MenuProps={menuProps}
                  >
                    <MenuItem value="__ALL__">
                      <ListItemIcon>
                        <Checkbox
                          checked={fileName.length === fileOptions.length}
                          indeterminate={
                            fileName.length > 0 &&
                            fileName.length < fileOptions.length
                          }
                        />
                      </ListItemIcon>
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {fileOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <ListItemIcon>
                          <Checkbox checked={fileName.includes(opt.value)} />
                        </ListItemIcon>
                        <ListItemText primary={opt.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Chat Window */}
              <Paper variant="outlined" className="ai-chatbot-chat-window">
                <Box className="ai-chatbot-messages" ref={chatWindowRef}>
                  {currentMessages?.length === 0 ? (
                    <Box className="ai-chatbot-empty-state">
                      <Typography variant="subtitle1" color="text.secondary">
                        Start by selecting document details and asking a
                        question.
                      </Typography>
                    </Box>
                  ) : (
                    currentMessages?.map((msg) => (
                      <Box
                        key={msg.id}
                        className={
                          "ai-chatbot-message-row " +
                          (msg.sender === "user" ? "user" : "bot")
                        }
                      >
                        <Box
                          className={
                            "ai-chatbot-avatar " +
                            (msg.sender === "user" ? "user" : "bot")
                          }
                        >
                          {msg.sender === "user" ? (
                            <PersonIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <SmartToyIcon sx={{ fontSize: 20 }} />
                          )}
                        </Box>

                        <Box
                          className={
                            "ai-chatbot-bubble " +
                            (msg.sender === "user" ? "user" : "bot")
                          }
                        >
                          {msg.sender === "bot" ? (
                            <div className="markdown-content">
                              <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                {msg.text}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <Typography variant="body2">{msg.text}</Typography>
                          )}
                        </Box>
                      </Box>
                    ))
                  )}

                  {showTyping && (
                    <Box className="ai-chatbot-typing">
                      <Box className="ai-chatbot-avatar bot ai-chatbot-typing-icon">
                        <SmartToyIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Assistant is typing
                        <span className="loading-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </span>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* Input */}
              <Box className="ai-chatbot-input-row">
                <TextField
                  className="ai-chatbot-input-field"
                  variant="outlined"
                  placeholder="Ask your question"
                  multiline
                  minRows={2}
                  maxRows={5}
                  fullWidth
                  value={queryText}
                  onChange={(e) => setQueryText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  error={!!errors.queryText}
                  helperText={errors.queryText}
                  size="small"
                />
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  size="medium"
                  sx={{
                    minWidth: 112,
                    backgroundColor: BRAND_PRIMARY,
                    "&:hover": { backgroundColor: BRAND_PRIMARY_HOVER },
                    "&:disabled": {
                      backgroundColor: BRAND_PRIMARY,
                      color: "#ffffff",
                      opacity: 0.8
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.6,
                      width: "100%"
                    }}
                  >
                    <SendIcon sx={{ fontSize: 18 }} />
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      Send
                    </Box>
                    <Box sx={{ width: 16, height: 16 }}>
                      {submitting && (
                        <CircularProgress size={16} color="inherit" />
                      )}
                    </Box>
                  </Box>
                </Button>
              </Box>
            </Box>

            {!isFloating && (
              <Box className="ai-chatbot-faq-wrapper">
                <Paper variant="outlined" className="ai-chatbot-faq-card">
                  <Box className="ai-chatbot-faq-header">
                    <HelpOutlineIcon sx={{ color: BRAND_PRIMARY }} />
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      sx={{ color: TEXT_SECONDARY }}
                    >
                      Sample Questions
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    {faqItems.map((faq, index) => (
                      <Box key={index} className="ai-chatbot-faq-item">
                        <Typography
                          variant="subtitle1"
                          fontWeight="500"
                          gutterBottom
                          sx={{ color: TEXT_SECONDARY }}
                          onClick={() => handleQuery(faq.question)}
                        >
                          {faq.question}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AIChatbotPage;
